import { supabase } from '@/lib/supabase';
import { unwrap, ServiceError } from '@/data/serviceError';
import { callEdgeFunction, fetchEdgeFunction } from '@/lib/edgeFunctions';
import type {
  AgentInstructionVersion,
  AgentPrestaties,
  AgentProposal,
  AgentRunStats,
  AiAgent,
  ProposalFilter,
  ProposalReviewBesluit,
} from '../types';

/** Sessiegegevens voor het live spraakgesprek: de server kiest de provider. */
export type ChiefVoiceSession =
  | {
      provider: 'openai';
      /** Ephemeral client secret; alleen geldig voor deze ene sessie (~10 min). */
      clientSecret: string;
      /** Realtime-model waarvoor het secret is uitgegeven. */
      model: string;
      /** Unix-tijdstip (seconden) waarop het secret verloopt, als bekend. */
      expiresAt: number | null;
    }
  | {
      provider: 'elevenlabs';
      /** Conversation-token voor `Conversation.startSession` (WebRTC). */
      conversationToken: string;
      /** ElevenLabs-agent waarvoor het token is uitgegeven. */
      agentId: string;
    };

/** Status van de Chief-spraakinrichting (uit `chief-voice-setup`). */
export interface ChiefVoiceSetupStatus {
  heeftKey: boolean;
  heeftVoice: boolean;
  heeftAgent: boolean;
  provider: 'elevenlabs' | 'openai';
}

/** Eén gegenereerde stemkandidaat (Voice Design-preview). */
export interface ChiefStemKandidaat {
  /** Stijl-slot (0–3) waarvoor deze kandidaat is ontworpen. */
  index: number;
  generatedVoiceId: string;
  /** Base64-audio van de proefzin (mp3), direct afspeelbaar via een data-URL. */
  audioBase64: string;
  omschrijving: string;
}

/** Uitkomst van `voer-voorstel-uit`: uitgevoerd, of (nog) niet ondersteund. */
export interface VoorstelUitvoerResultaat {
  uitgevoerd: boolean;
  /** Informatieve melding wanneer automatische uitvoering (nog) niet kan (501). */
  melding: string | null;
}

/** Callbacks voor de streaming-variant van de Chief-vraag. */
export interface ChiefStreamCallbacks {
  /** Modelronde gestart (`fase: 'denken'`) of een tool draait (`fase: 'tool'` + toolnaam). */
  onStatus?: (fase: string, tool?: string) => void;
  /** Live tekstfragment van het antwoord. */
  onDelta?: (text: string) => void;
  /** Gooi de lopende antwoordtekst weg (er volgde toch een tool-ronde). */
  onReset?: () => void;
}

/** Eén SSE-event van de `chief`-edge function (losjes getypt: server is extern). */
interface ChiefStreamEvent {
  type?: string;
  fase?: string;
  tool?: string;
  text?: string;
  antwoord?: string;
  melding?: string;
}

function parseSseEvent(rawEvent: string): ChiefStreamEvent | null {
  const data = rawEvent
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('\n');
  if (!data) return null;
  try {
    return JSON.parse(data) as ChiefStreamEvent;
  } catch {
    return null; // half of kapot event; overslaan
  }
}

function startOfMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

function startOfTodayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Data-toegang voor de AI-organisatie (agent_registry, agent_proposals,
 * agent_runs, agent_instruction_versions, chief_data_gaps + de Chief-edge
 * function). De enige plek die deze tabellen aanspreekt; componenten
 * consumeren dit via de hooks in `../hooks/useAiOrg`.
 */
export const aiOrgService = {
  async listAgents(): Promise<AiAgent[]> {
    return unwrap(
      await supabase
        .from('agent_registry')
        .select('*')
        .order('domein', { ascending: true })
        .order('code', { ascending: true }),
      'Agents laden',
    );
  },

  async getAgent(code: string): Promise<AiAgent | null> {
    const { data, error } = await supabase
      .from('agent_registry')
      .select('*')
      .eq('code', code)
      .maybeSingle();
    if (error) throw new ServiceError(`Agent laden: ${error.message}`, error.code);
    return data;
  },

  async listInstructieVersies(agentCode: string): Promise<AgentInstructionVersion[]> {
    return unwrap(
      await supabase
        .from('agent_instruction_versions')
        .select('*')
        .eq('agent_code', agentCode)
        .order('versie', { ascending: false }),
      'Instructieversies laden',
    );
  },

  /**
   * Run-cijfers per agent voor de registry-lijst: kosten in de huidige maand
   * en het moment van de laatste run. Kijkt 90 dagen terug (ruim genoeg voor
   * "laatste run" bij de startbezetting) en aggregeert client-side.
   */
  async runStats(): Promise<AgentRunStats> {
    const rows = unwrap(
      await supabase
        .from('agent_runs')
        .select('agent_code, kosten, created_at')
        .gte('created_at', daysAgoIso(90))
        .order('created_at', { ascending: false })
        .limit(5000),
      'Run-statistieken laden',
    );

    const monthStart = startOfMonthIso();
    const perAgent: AgentRunStats['perAgent'] = {};
    let kostenMaandTotaal = 0;

    for (const row of rows) {
      const stat = (perAgent[row.agent_code] ??= { kostenMaand: 0, laatsteRun: null });
      // Rijen zijn aflopend gesorteerd: de eerste per agent is de laatste run.
      if (!stat.laatsteRun) stat.laatsteRun = row.created_at;
      if (row.created_at >= monthStart) {
        stat.kostenMaand += row.kosten;
        kostenMaandTotaal += row.kosten;
      }
    }

    return { perAgent, kostenMaandTotaal };
  },

  /** Prestaties van één agent over de laatste 30 dagen (runs + voorstellen). */
  async agentPrestaties(agentCode: string): Promise<AgentPrestaties> {
    const since = daysAgoIso(30);

    const [runs, proposals] = await Promise.all([
      supabase
        .from('agent_runs')
        .select('kosten, uitkomst')
        .eq('agent_code', agentCode)
        .gte('created_at', since)
        .limit(2000),
      supabase
        .from('agent_proposals')
        .select('status')
        .eq('agent_code', agentCode)
        .gte('created_at', since)
        .limit(1000),
    ]);

    const runRows = unwrap(runs, 'Agent-runs laden');
    const proposalRows = unwrap(proposals, 'Agent-voorstellen laden');

    return {
      runs: runRows.length,
      kosten: runRows.reduce((sum, r) => sum + r.kosten, 0),
      fouten: runRows.filter((r) => r.uitkomst === 'fout').length,
      voorstellen: proposalRows.length,
      goedgekeurd: proposalRows.filter(
        (p) => p.status === 'goedgekeurd' || p.status === 'uitgevoerd',
      ).length,
      afgewezen: proposalRows.filter((p) => p.status === 'afgewezen').length,
    };
  },

  /** Voorstellen: openstaand, of vandaag afgehandeld. */
  async listProposals(filter: ProposalFilter): Promise<AgentProposal[]> {
    if (filter === 'open') {
      return unwrap(
        await supabase
          .from('agent_proposals')
          .select('*')
          .eq('status', 'open')
          .order('created_at', { ascending: false })
          .limit(200),
        'Voorstellen laden',
      );
    }
    return unwrap(
      await supabase
        .from('agent_proposals')
        .select('*')
        .neq('status', 'open')
        .gte('reviewed_at', startOfTodayIso())
        .order('reviewed_at', { ascending: false })
        .limit(200),
      'Afgehandelde voorstellen laden',
    );
  },

  async countOpenProposals(): Promise<number> {
    const { count, error } = await supabase
      .from('agent_proposals')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'open');
    if (error) throw new ServiceError(`Open voorstellen tellen: ${error.message}`, error.code);
    return count ?? 0;
  },

  /**
   * Voorstel afhandelen. Afwijzen zonder reden is verboden — de reden is
   * trainingsdata (bouwboek H5.2) en de DB dwingt dit ook af.
   */
  async reviewProposal(input: {
    id: string;
    besluit: ProposalReviewBesluit;
    reviewerId: string;
    reden?: string;
  }): Promise<void> {
    const reden = input.reden?.trim() || null;
    if (input.besluit === 'afgewezen' && !reden) {
      throw new ServiceError('Een reden is verplicht bij afwijzen.');
    }
    const { error } = await supabase
      .from('agent_proposals')
      .update({
        status: input.besluit,
        review_reden: reden,
        reviewer: input.reviewerId,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', input.id)
      .eq('status', 'open');
    if (error) throw new ServiceError(`Voorstel afhandelen: ${error.message}`, error.code);
  },

  /**
   * Voer een goedgekeurd voorstel uit via de edge function `voer-voorstel-uit`
   * (Admin). HTTP 501 betekent "dit type kent nog geen automatische
   * uitvoering": het voorstel blijft gewoon goedgekeurd en we geven een
   * informatieve melding terug in plaats van een fout.
   */
  async voerVoorstelUit(proposalId: string): Promise<VoorstelUitvoerResultaat> {
    const response = await fetchEdgeFunction('voer-voorstel-uit', { proposal_id: proposalId });
    const text = await response.text();
    let result: Record<string, unknown> = {};
    try {
      result = JSON.parse(text) as Record<string, unknown>;
    } catch {
      /* geen JSON-body; status is leidend */
    }

    if (response.status === 501) {
      const melding = result.melding ?? result.message ?? result.error;
      return {
        uitgevoerd: false,
        melding:
          typeof melding === 'string' && melding.trim()
            ? melding
            : 'Goedgekeurd; automatische uitvoering voor dit type volgt binnenkort.',
      };
    }
    if (!response.ok) {
      throw new ServiceError(
        String(
          result.error ||
            result.message ||
            result.msg ||
            `Voorstel uitvoeren mislukt (${response.status})`,
        ),
      );
    }
    return { uitgevoerd: true, melding: null };
  },

  /**
   * Stel Chief een vraag via de `chief`-edge function met SSE-streaming.
   * Deltas/status komen via de callbacks binnen; het `klaar`-event levert de
   * volledige antwoordtekst (bron van waarheid) als resultaat. Setup-fouten
   * (401/validatie) komen als gewone JSON terug; die gooien we als Error zodat
   * de aanroeper kan terugvallen op het niet-streaming pad.
   */
  async vraagChiefStream(
    vraag: string,
    historie: Array<{ role: 'user' | 'assistant'; content: string }>,
    callbacks: ChiefStreamCallbacks,
  ): Promise<string> {
    const response = await fetchEdgeFunction('chief', { vraag, historie, stream: true });
    const contentType = response.headers.get('content-type') ?? '';

    // Geen stream (foutstatus óf gewone JSON): afhandelen zoals het JSON-pad.
    if (!response.ok || !contentType.includes('text/event-stream')) {
      const text = await response.text();
      let result: Record<string, unknown>;
      try {
        result = JSON.parse(text) as Record<string, unknown>;
      } catch {
        throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
      }
      if (!response.ok) {
        throw new Error(
          String(
            result.error || result.message || result.msg || `Verzoek mislukt (${response.status})`,
          ),
        );
      }
      const antwoord = result.antwoord ?? result.answer ?? result.message;
      if (typeof antwoord !== 'string' || !antwoord.trim()) {
        throw new Error('Chief gaf een leeg antwoord terug. Probeer het opnieuw.');
      }
      return antwoord;
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Streamen wordt niet ondersteund in deze browser.');

    const decoder = new TextDecoder();
    let buffer = '';
    let lopend = ''; // tot nu toe gestreamde antwoordtekst (na laatste reset)
    let antwoord: string | null = null;

    const verwerk = (ev: ChiefStreamEvent): boolean => {
      switch (ev.type) {
        case 'status':
          callbacks.onStatus?.(ev.fase ?? 'denken', ev.tool);
          return false;
        case 'delta': {
          const text = ev.text ?? '';
          if (text) {
            lopend += text;
            callbacks.onDelta?.(text);
          }
          return false;
        }
        case 'reset':
          lopend = '';
          callbacks.onReset?.();
          return false;
        case 'klaar':
          antwoord = typeof ev.antwoord === 'string' ? ev.antwoord : null;
          return true;
        case 'fout':
          throw new Error(ev.melding || 'Chief kon niet antwoorden.');
        default:
          return false; // onbekend event; negeren
      }
    };

    try {
      let klaar = false;
      while (!klaar) {
        const { done, value } = await reader.read();
        if (done) {
          buffer += decoder.decode();
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let scheiding = buffer.indexOf('\n\n');
        while (!klaar && scheiding >= 0) {
          const ev = parseSseEvent(buffer.slice(0, scheiding));
          buffer = buffer.slice(scheiding + 2);
          if (ev && verwerk(ev)) klaar = true;
          scheiding = buffer.indexOf('\n\n');
        }
      }
      // Restje zonder afsluitende lege regel (bijv. `klaar` vlak voor EOF).
      if (!klaar && buffer.trim()) {
        const ev = parseSseEvent(buffer);
        if (ev) verwerk(ev);
      }
    } finally {
      reader.cancel().catch(() => undefined);
    }

    const definitief = antwoord ?? (lopend.trim() ? lopend : null);
    if (!definitief || !definitief.trim()) {
      throw new Error('Chief gaf een leeg antwoord terug. Probeer het opnieuw.');
    }
    return definitief;
  },

  /**
   * Start een live spraaksessie: de `chief-voice-session`-edge function geeft
   * een kortlevend OpenAI Realtime-token uit (de OpenAI-sleutel zelf blijft
   * server-side). De browser zet daarmee de WebRTC-verbinding op; zie
   * `../lib/realtimeVoice.ts`.
   */
  async startVoiceSession(): Promise<ChiefVoiceSession> {
    const result = await callEdgeFunction('chief-voice-session', {});

    if (result.provider === 'elevenlabs') {
      const conversationToken = result.conversation_token;
      const agentId = result.agent_id;
      if (
        typeof conversationToken !== 'string' ||
        !conversationToken ||
        typeof agentId !== 'string' ||
        !agentId
      ) {
        throw new Error('Spraaksessie-antwoord (ElevenLabs) was onvolledig. Probeer het opnieuw.');
      }
      return { provider: 'elevenlabs', conversationToken, agentId };
    }

    // Provider 'openai' — of een oudere function-versie zonder provider-veld.
    const clientSecret = result.client_secret;
    const model = result.model;
    if (typeof clientSecret !== 'string' || !clientSecret || typeof model !== 'string' || !model) {
      throw new Error('Spraaksessie-antwoord was onvolledig. Probeer het opnieuw.');
    }
    return {
      provider: 'openai',
      clientSecret,
      model,
      expiresAt: typeof result.expires_at === 'number' ? result.expires_at : null,
    };
  },

  /* ------------------- Chief-stem (chief-voice-setup) ------------------- */

  /** Status van de spraakinrichting: key/stem/agent aanwezig + actieve provider. */
  async voiceSetupStatus(): Promise<ChiefVoiceSetupStatus> {
    const result = await callEdgeFunction('chief-voice-setup', { actie: 'status' });
    return {
      heeftKey: result.heeft_key === true,
      heeftVoice: result.heeft_voice === true,
      heeftAgent: result.heeft_agent === true,
      provider: result.provider === 'elevenlabs' ? 'elevenlabs' : 'openai',
    };
  },

  /**
   * Genereer stemkandidaten (Voice Design). Zonder `indices` alle vier;
   * met `indices` (stijl-slots 0–3) alleen die slots — voor "vervang
   * niet-bewaarde stemmen". Elke kandidaat draagt zijn slot als `index`.
   */
  async voiceKandidaten(indices?: number[]): Promise<ChiefStemKandidaat[]> {
    const result = await callEdgeFunction('chief-voice-setup', {
      actie: 'kandidaten',
      ...(indices && indices.length > 0 ? { indices } : {}),
    });
    const lijst = Array.isArray(result.kandidaten)
      ? result.kandidaten
      : Array.isArray(result)
        ? (result as unknown[])
        : [];
    const kandidaten: ChiefStemKandidaat[] = [];
    for (const [positie, item] of (lijst as unknown[]).entries()) {
      if (typeof item !== 'object' || item === null) continue;
      const record = item as Record<string, unknown>;
      if (
        typeof record.generated_voice_id === 'string' &&
        typeof record.audio_base64 === 'string'
      ) {
        kandidaten.push({
          // Oudere function-versies sturen nog geen `index`: dan is de
          // positie in het (aangevraagde) lijstje het beste slot-signaal.
          index:
            typeof record.index === 'number' && Number.isInteger(record.index)
              ? record.index
              : (indices?.[positie] ?? positie),
          generatedVoiceId: record.generated_voice_id,
          audioBase64: record.audio_base64,
          omschrijving: typeof record.omschrijving === 'string' ? record.omschrijving : '',
        });
      }
    }
    if (kandidaten.length === 0) {
      throw new Error('Er kwamen geen stemkandidaten terug. Probeer het opnieuw.');
    }
    return kandidaten;
  },

  /** Maak deze kandidaat definitief: "Deze wordt Chief". */
  async kiesVoice(generatedVoiceId: string, omschrijving: string): Promise<void> {
    await callEdgeFunction('chief-voice-setup', {
      actie: 'kies',
      generated_voice_id: generatedVoiceId,
      omschrijving,
    });
  },

  /** Bestaande ElevenLabs-(bibliotheek)stem direct als Chief instellen. */
  async kiesBestaandeStem(voiceId: string): Promise<void> {
    await callEdgeFunction('chief-voice-setup', {
      actie: 'kies_bestaand',
      voice_id: voiceId.trim(),
    });
  },

  /** Actieve spraakprovider wisselen (ElevenLabs / OpenAI-fallback). */
  async zetVoiceProvider(provider: 'elevenlabs' | 'openai'): Promise<void> {
    await callEdgeFunction('chief-voice-setup', { actie: 'provider', provider });
  },

  /**
   * Bestaande ElevenLabs-agent bijwerken met de actuele persona en tools
   * (nodig na deploys waarin die wijzigen).
   */
  async syncVoiceAgent(): Promise<void> {
    await callEdgeFunction('chief-voice-setup', { actie: 'sync' });
  },

  /** Stel Chief een vraag via de `chief`-edge function. */
  async vraagChief(
    vraag: string,
    historie: Array<{ role: 'user' | 'assistant'; content: string }>,
  ): Promise<string> {
    const result = await callEdgeFunction('chief', { vraag, historie });
    const antwoord = result.antwoord ?? result.answer ?? result.message;
    if (typeof antwoord !== 'string' || !antwoord.trim()) {
      throw new Error('Chief gaf een leeg antwoord terug. Probeer het opnieuw.');
    }
    return antwoord;
  },
};
